/**
 * schema.org JSON-LD builders.
 *
 * CommonJS so the build-time SEO script can require it. Every builder returns a plain object; the
 * caller serialises it.
 *
 * Scope note: only claims that the page actually backs up are emitted. Rating and offer counts are
 * left to runtime builders that have real data, because inventing an AggregateRating is a
 * structured-data violation and risks a manual action.
 *
 * There is deliberately no WebSite SearchAction: a sitelinks searchbox has to point at a real search
 * results URL and this app resolves search client-side, so there is nothing valid to target yet.
 */

const {
  SITE_NAME,
  SITE_ORIGIN,
  DEFAULT_OG_IMAGE,
  SERVICE_LABELS,
  getGameName,
  getCurrencyTerm,
  getTopUpTerm,
  absoluteUrl,
} = require('./metadata');
const { FAQ_ITEMS } = require('./faqItems');

const ORGANIZATION_ID = `${SITE_ORIGIN.replace(/\/$/, '')}/#organization`;
const WEBSITE_ID = `${SITE_ORIGIN.replace(/\/$/, '')}/#website`;

function buildOrganization() {
  return {
    '@type': 'Organization',
    '@id': ORGANIZATION_ID,
    name: SITE_NAME,
    alternateName: 'XMARKET',
    url: absoluteUrl('/'),
    logo: {
      '@type': 'ImageObject',
      url: absoluteUrl(DEFAULT_OG_IMAGE),
    },
    description:
      'Player-to-player marketplace for game accounts, in-game currency, items, top-ups and boosting services, with escrow-protected payments.',
    sameAs: ['https://www.trustpilot.com/review/xmarket.gg'],
    contactPoint: {
      '@type': 'ContactPoint',
      contactType: 'customer support',
      email: 'support@xmarket.gg',
      availableLanguage: ['English'],
    },
  };
}

function buildWebSite() {
  return {
    '@type': 'WebSite',
    '@id': WEBSITE_ID,
    name: SITE_NAME,
    url: absoluteUrl('/'),
    publisher: { '@id': ORGANIZATION_ID },
    inLanguage: 'en',
  };
}

function buildFaqPage() {
  return {
    '@type': 'FAQPage',
    mainEntity: FAQ_ITEMS.map((item) => ({
      '@type': 'Question',
      name: item.question,
      acceptedAnswer: {
        '@type': 'Answer',
        text: item.answer,
      },
    })),
  };
}

/**
 * Breadcrumb trail matching the site hierarchy: Home > Game > Service.
 *
 * @param {{ pageType: string, slug: string|null }} page
 */
function buildBreadcrumbs(page) {
  const items = [{ name: 'Home', item: absoluteUrl('/') }];

  if (page.slug) {
    items.push({ name: getGameName(page.slug), item: absoluteUrl(`/${page.slug}`) });

    const serviceLabel = SERVICE_LABELS[page.pageType];
    if (serviceLabel) {
      items.push({
        name: serviceLabel,
        item: absoluteUrl(`/${page.slug}/${page.pageType}`),
      });
    }
  }

  if (items.length < 2) return null;

  return {
    '@type': 'BreadcrumbList',
    itemListElement: items.map((entry, index) => ({
      '@type': 'ListItem',
      position: index + 1,
      name: entry.name,
      item: entry.item,
    })),
  };
}

/** What a catalog page lists, phrased for the page type. */
function describeCollection(page) {
  const name = getGameName(page.slug);
  switch (page.pageType) {
    case 'currency':
      return `${getCurrencyTerm(page.slug)} offers from verified sellers`;
    case 'accounts':
      return `${name} accounts for sale from verified sellers`;
    case 'items':
      return `${name} items and skins for sale from verified sellers`;
    case 'top-ups':
      return `${getTopUpTerm(page.slug)} top-up offers with instant delivery`;
    case 'boosting':
      return `${name} boosting and coaching services from verified boosters`;
    default:
      return `${name} accounts, currency, items and boosting services`;
  }
}

function buildCollectionPage(page, meta, canonical) {
  return {
    '@type': 'CollectionPage',
    '@id': `${canonical}#page`,
    url: canonical,
    name: meta.title,
    description: describeCollection(page),
    isPartOf: { '@id': WEBSITE_ID },
    inLanguage: 'en',
    about: {
      '@type': 'VideoGame',
      name: getGameName(page.slug),
    },
  };
}

/** Boosting pages sell a service, so they get Service on top of CollectionPage. */
function buildBoostingService(page, canonical) {
  const name = getGameName(page.slug);
  return {
    '@type': 'Service',
    '@id': `${canonical}#service`,
    name: `${name} Boosting Services`,
    serviceType: `${name} rank boosting, coaching and carry services`,
    description: `Hire verified ${name} boosters for rank boosting, coaching and carries with escrow-protected payment.`,
    provider: { '@id': ORGANIZATION_ID },
    areaServed: 'Worldwide',
  };
}

/**
 * Full JSON-LD graph for a static catalog page.
 *
 * @param {{ pageType: string, slug: string|null }} page
 * @param {{ title: string, description: string }} meta
 * @param {string} canonical Absolute canonical URL
 * @returns {object} A single @graph document, which keeps one script tag per page.
 */
function buildPageGraph(page, meta, canonical) {
  const graph = [];

  if (page.pageType === 'home') {
    graph.push(buildOrganization(), buildWebSite(), buildFaqPage());
    return { '@context': 'https://schema.org', '@graph': graph };
  }

  graph.push(buildOrganization(), buildWebSite());

  const breadcrumbs = buildBreadcrumbs(page);
  if (breadcrumbs) graph.push(breadcrumbs);

  graph.push(buildCollectionPage(page, meta, canonical));

  if (page.pageType === 'boosting') {
    graph.push(buildBoostingService(page, canonical));
  }

  return { '@context': 'https://schema.org', '@graph': graph };
}

/**
 * Product graph for a single listing.
 *
 * aggregateRating is emitted only when there are real reviews to back it. The UI falls back to a
 * flattering default rating when a seller has no history, and publishing that as structured data
 * would be a rating we cannot substantiate — grounds for a manual action, not just lost rich results.
 *
 * @param {object} ad Row from seller_ads, with `title`, `price`, `currency`, `game`, `service_type`
 * @param {object} opts
 * @param {string} opts.canonical Absolute URL of the listing
 * @param {string[]} [opts.images] Absolute image URLs
 * @param {string} [opts.sellerName]
 * @param {number} [opts.rating] Percentage score, e.g. 98.5
 * @param {number} [opts.reviewCount]
 * @returns {object}
 */
function buildProductGraph(ad, { canonical, images = [], sellerName, rating, reviewCount = 0 }) {
  const gameName = getGameName(ad.game);
  const serviceLabel = SERVICE_LABELS[ad.service_type] || 'Items';
  const price = Number(ad.price);

  const product = {
    '@type': 'Product',
    '@id': `${canonical}#product`,
    name: ad.title,
    description:
      ad.description ||
      `${ad.title} — ${gameName} ${serviceLabel.toLowerCase()} from a verified seller on ${SITE_NAME}.`,
    category: `${gameName} ${serviceLabel}`,
    url: canonical,
  };

  if (images.length > 0) product.image = images;

  if (Number.isFinite(price) && price > 0) {
    product.offers = {
      '@type': 'Offer',
      url: canonical,
      price: price.toFixed(2),
      priceCurrency: ad.currency || 'EUR',
      availability:
        Number(ad.quantity) > 0
          ? 'https://schema.org/InStock'
          : 'https://schema.org/OutOfStock',
      seller: sellerName
        ? { '@type': 'Person', name: sellerName }
        : { '@id': ORGANIZATION_ID },
    };
  }

  if (Number.isFinite(rating) && Number(reviewCount) > 0) {
    product.aggregateRating = {
      '@type': 'AggregateRating',
      // Seller scores are percentages; schema.org wants a value on a stated scale.
      ratingValue: (Math.max(0, Math.min(100, rating)) / 20).toFixed(1),
      bestRating: '5',
      worstRating: '1',
      reviewCount: Number(reviewCount),
    };
  }

  const breadcrumbs = {
    '@type': 'BreadcrumbList',
    itemListElement: [
      { '@type': 'ListItem', position: 1, name: 'Home', item: absoluteUrl('/') },
      {
        '@type': 'ListItem',
        position: 2,
        name: gameName,
        item: absoluteUrl(`/${ad.game}`),
      },
      {
        '@type': 'ListItem',
        position: 3,
        name: serviceLabel,
        item: absoluteUrl(`/${ad.game}/${ad.service_type}`),
      },
      { '@type': 'ListItem', position: 4, name: ad.title, item: canonical },
    ],
  };

  return {
    '@context': 'https://schema.org',
    '@graph': [buildOrganization(), buildWebSite(), breadcrumbs, product],
  };
}

module.exports = {
  ORGANIZATION_ID,
  WEBSITE_ID,
  buildOrganization,
  buildWebSite,
  buildFaqPage,
  buildBreadcrumbs,
  buildCollectionPage,
  buildBoostingService,
  buildPageGraph,
  buildProductGraph,
};
